import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

function PriceChart({ competitors }) {
  // Transform data for the chart, including a per-date average price series
  const chartData = React.useMemo(() => {
    if (!competitors || competitors.length === 0) return [];

    // Get all unique dates from all competitors
    const allDates = new Set();
    competitors.forEach(comp => {
      if (comp.price_history) {
        comp.price_history.forEach(ph => allDates.add(ph.date));
      }
    });

    const sortedDates = Array.from(allDates).sort();

    // Build data points for each date, including per-date average across all competitors
    return sortedDates.map(date => {
      const dataPoint = { date };
      competitors.forEach(comp => {
        const historyItem = comp.price_history?.find(ph => ph.date === date);
        dataPoint[comp.name] = historyItem ? historyItem.price : null;
      });
      const prices = competitors
        .map(c => dataPoint[c.name])
        .filter(p => p !== null && p !== undefined);
      dataPoint.__avgPrice = prices.length > 0
        ? parseFloat((prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(2))
        : null;
      return dataPoint;
    });
  }, [competitors]);

  // Index of the last non-null avg price point (for label placement)
  const lastAvgIndex = React.useMemo(() => {
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (chartData[i].__avgPrice !== null) return i;
    }
    return -1;
  }, [chartData]);

  // Generate distinct colors for each competitor
  const colors = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // green
    '#f59e0b', // amber
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#374151' }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            entry.value !== null && entry.name !== '__avgPrice' && (
              <p key={index} style={{ margin: '4px 0', fontSize: '13px', color: entry.color }}>
                {entry.name}: ${entry.value.toFixed(2)}
              </p>
            )
          ))}
          {payload.find(e => e.name === '__avgPrice' && e.value !== null) && (
            <p style={{ margin: '4px 0', fontSize: '13px', color: '#ef4444', borderTop: '1px solid #e5e7eb', paddingTop: '4px' }}>
              Avg Price: ${payload.find(e => e.name === '__avgPrice').value.toFixed(2)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom label that renders only on the last avg price data point, positioned above
  const AvgPriceLabel = ({ x, y, index }) => {
    if (index !== lastAvgIndex) return null;
    return (
      <text x={x} y={y - 10} fill="#ef4444" fontSize={11} textAnchor="middle" fontWeight="600">
        Avg Price
      </text>
    );
  };

  if (competitors.length === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: '#6b7280',
      }}>
        <p>No price history data available</p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
        Price History
      </h3>

      <div style={{ height: '350px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              stroke="#6b7280"
              fontSize={12}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
              }}
            />
            <YAxis
              stroke="#6b7280"
              fontSize={12}
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend formatter={(value) => value === '__avgPrice' ? 'Avg Price' : value} />

            {/* Per-date average price line */}
            <Line
              type="monotone"
              dataKey="__avgPrice"
              name="__avgPrice"
              stroke="#ef4444"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4, fill: '#ef4444', stroke: '#ef4444' }}
              activeDot={{ r: 6 }}
              connectNulls
              label={<AvgPriceLabel />}
            />

            {competitors.map((comp, index) => (
              <Line
                key={comp.name}
                type="monotone"
                dataKey={comp.name}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ marginTop: '16px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {competitors.map((comp, index) => (
          <div key={comp.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: colors[index % colors.length],
            }} />
            <span style={{ fontSize: '12px', color: '#374151' }}>
              {comp.name}: ${comp.current_price?.toFixed(2) || 'N/A'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PriceChart;
